import { useEffect, useState } from 'react';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/atoms/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/atoms/popover';
import {
  useEquipmentDescription,
  useEquipmentDescriptions,
} from '@/hooks/equipment/useEquipmentDescriptions';
import { useEquipmentDescriptionOptions } from '@/hooks/equipment/useEquipmentDescriptionOptions';
import type { EquipmentDescription } from '@/types/equipment-description';

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

/**
 * Searchable, id-resolving combobox over ACTIVE equipment descriptions.
 *
 * (a) Server-side debounced search reaches descriptions beyond the first page,
 * and (b) the current selection is resolved by id so the trigger label renders
 * correctly even when the linked description isn't in the current search page.
 * Stores the description **id** ('' = no link).
 *
 * Mounts/fetches only when `enabled`.
 */
export function EquipmentDescriptionCombobox({
  value,
  onChange,
  onSelectDescription,
  enabled,
  category,
  categories,
  unlinkedOnly = false,
  required = false,
}: {
  value: string;
  onChange: (id: string) => void;
  /**
   * Fires with the full selected type (or null when cleared), so callers can
   * prepopulate fields (e.g. name/code) from the picked type.
   */
  onSelectDescription?: (description: EquipmentDescription | null) => void;
  enabled: boolean;
  /** When set, restricts results to descriptions in this category. */
  category?: string;
  /** When provided, combine results from only these categories. */
  categories?: string[];
  /** Only offer types not yet linked to an active pricebook item. */
  unlinkedOnly?: boolean;
  /** Hide the "None" option — the selection is mandatory. */
  required?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedQuery = useDebounce(search, 300);

  const query = useEquipmentDescriptions(
    { status: 'ACTIVE', category, search: debouncedQuery, unlinkedOnly, limit: 50 },
    enabled && categories === undefined,
  );
  const categoryQuery = useEquipmentDescriptionOptions(
    categories ?? [],
    { status: 'ACTIVE', search: debouncedQuery, unlinkedOnly, limit: 50 },
    enabled && categories !== undefined,
  );
  const items = categories === undefined ? (query.data?.items ?? []) : (categoryQuery.data ?? []);
  const isLoading = categories === undefined ? query.isLoading : categoryQuery.isLoading;
  const itemsContainCurrent = items.some((d) => d.id === value);

  // Resolve the current selection by id when it's outside the current search
  // page, so the trigger can still show its label.
  const resolvedQuery = useEquipmentDescription(value, enabled && !!value && !itemsContainCurrent);

  const selected =
    items.find((d) => d.id === value) ??
    (resolvedQuery.data?.id === value ? resolvedQuery.data : undefined);
  const selectedLabel = selected ? `${selected.code} — ${selected.name}` : null;

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) setSearch('');
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between bg-background font-normal"
        >
          <span className="truncate text-sm">
            {selectedLabel ?? <span className="text-muted-foreground">Select equipment type</span>}
          </span>
          <ChevronsUpDown size={14} className="ml-2 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search equipment types..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {isLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 size={14} className="animate-spin text-muted-foreground" />
              </div>
            ) : (
              <>
                <CommandEmpty>No equipment types found.</CommandEmpty>
                <CommandGroup>
                  {required ? null : (
                    <CommandItem
                      value="__none__"
                      onSelect={() => {
                        onChange('');
                        onSelectDescription?.(null);
                        setOpen(false);
                        setSearch('');
                      }}
                    >
                      <Check size={14} className={value === '' ? 'opacity-100' : 'opacity-0'} />
                      <span className="text-muted-foreground">None</span>
                    </CommandItem>
                  )}
                  {items.map((d) => (
                    <CommandItem
                      key={d.id}
                      value={d.id}
                      onSelect={() => {
                        onChange(d.id);
                        onSelectDescription?.(d);
                        setOpen(false);
                        setSearch('');
                      }}
                    >
                      <Check size={14} className={value === d.id ? 'opacity-100' : 'opacity-0'} />
                      <span className="truncate">
                        {d.code} — {d.name}
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
